import { isJson } from 'utils/isJson';
import type { CrmArrayLeaf } from './crmArrayPayload';

export interface TipTapJsonNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TipTapJsonNode[];
  text?: string;
  [key: string]: unknown;
}

export const chipNode = (leaf: CrmArrayLeaf): TipTapJsonNode => ({
  type: 'variable',
  attrs: {
    name: leaf.name,
    friendlyName: leaf.friendlyName ?? leaf.name,
    objectId: leaf.objectId,
    path: leaf.path,
    template: leaf.template,
  },
});

export const instanceChipNode = (leaf: CrmArrayLeaf): TipTapJsonNode => ({
  type: 'variable',
  attrs: {
    name: leaf.name,
    friendlyName: leaf.friendlyName ?? leaf.name,
    objectId: leaf.objectId,
    path: leaf.path,
    template: leaf.template,
  },
});

export const stampInstanceBinding = (chip: TipTapJsonNode, instance: CrmArrayLeaf): TipTapJsonNode => ({
  ...chip,
  attrs: { ...chip.attrs, objectId: instance.objectId, path: instance.path, template: instance.template },
});

export const chipsWithSeparators = (leaves: CrmArrayLeaf[]): TipTapJsonNode[] =>
  leaves.flatMap((leaf, index) => (index > 0 ? [{ type: 'text', text: ', ' }, chipNode(leaf)] : [chipNode(leaf)]));

export const chipCellDoc = (leaf: CrmArrayLeaf): string =>
  JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: [chipNode(leaf)] }] });

export const parseTipTapDoc = (value: unknown): TipTapJsonNode | null => {
  if (!value || typeof value !== 'string' || !isJson(value)) return null;
  const parsed = JSON.parse(value) as unknown;
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  return parsed as TipTapJsonNode;
};

export const mergeChipIntoStoredValue = (value: unknown, leaf: CrmArrayLeaf): string => {
  if (!value || typeof value !== 'string') return chipCellDoc(leaf);
  const doc = parseTipTapDoc(value);
  if (doc) {
    const content = Array.isArray(doc.content) ? [...doc.content] : [];
    const last = content[content.length - 1];
    if (last?.type === 'paragraph') {
      const paragraphContent = Array.isArray(last.content) ? [...last.content] : [];
      if (paragraphContent.length > 0) paragraphContent.push({ type: 'text', text: ' ' });
      paragraphContent.push(chipNode(leaf));
      content[content.length - 1] = { ...last, content: paragraphContent };
    } else {
      content.push({ type: 'paragraph', content: [chipNode(leaf)] });
    }
    return JSON.stringify({ ...doc, type: 'doc', content });
  }
  const existingText = value.replace(/<[^>]*>/g, '').trim();
  const paragraphContent: TipTapJsonNode[] = existingText ? [{ type: 'text', text: `${existingText} ` }, chipNode(leaf)] : [chipNode(leaf)];
  return JSON.stringify({ type: 'doc', content: [{ type: 'paragraph', content: paragraphContent }] });
};

export const transformChips = (
  node: TipTapJsonNode,
  mapper: (chipName: string, chipTemplate?: string) => TipTapJsonNode | null | undefined
): TipTapJsonNode | null => {
  if (node.type === 'variable') {
    if (node.attrs?.isConflict) return { ...node };
    const chipName = String(node.attrs?.name ?? '');
    const chipTemplate = node.attrs?.template ? String(node.attrs.template) : undefined;
    const mapped = mapper(chipName, chipTemplate);
    if (mapped === null) return null;
    return mapped ?? { ...node };
  }
  const cloned: TipTapJsonNode = { ...node };
  if (Array.isArray(node.content)) {
    cloned.content = node.content.map((child) => transformChips(child, mapper)).filter((child): child is TipTapJsonNode => child !== null);
  }
  return cloned;
};

export const chipNamesIn = (value: unknown): { name: string; path?: string; template?: string }[] => {
  if (!value || typeof value !== 'string' || !isJson(value)) return [];
  const names: { name: string; path?: string; template?: string }[] = [];
  const walk = (node: TipTapJsonNode) => {
    if (node.type === 'variable' && !node.attrs?.isConflict) {
      names.push({
        name: String(node.attrs?.name ?? ''),
        path: node.attrs?.path ? String(node.attrs.path) : undefined,
        template: node.attrs?.template ? String(node.attrs.template) : undefined,
      });
    }
    if (Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(JSON.parse(value) as TipTapJsonNode);
  return names;
};
